import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useMemo } from 'react';

import { Flex, Icon, Text } from '~/components/blocks';
import { NotificationListItem } from '~/components/partials/NotificationListItem';
import { AnsweredQuestionnaireSheetFragment, DirectVisitor } from '~/graphql/generated/graphql';
import { notificationPopoverState } from '~/state/layouts/SharedAppShell/atoms';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { Label } from '~/utils/label';

type Props = {
  sheet: AnsweredQuestionnaireSheetFragment;
};

export const Notification = React.memo((props: Props) => {
  const { sheet } = props;
  const theme = useTheme();

  const patient = useMemo(() => {
    const messageDeliverGuestPatient =
      sheet.medicationFollowupMessage.messageDeliverResult?.guestPatient?.__typename ===
      'DirectVisitor'
        ? (sheet.medicationFollowupMessage.messageDeliverResult.guestPatient as DirectVisitor)
        : null;
    return (
      sheet.medicationFollowupMessage.patient ||
      sheet.medicationFollowupMessage.webVisitor ||
      sheet.medicationFollowupMessage.directVisitor ||
      sheet.medicationFollowupMessage.lineVisitor ||
      messageDeliverGuestPatient
    );
  }, [
    sheet.medicationFollowupMessage.patient,
    sheet.medicationFollowupMessage.webVisitor,
    sheet.medicationFollowupMessage.messageDeliverResult?.guestPatient,
    sheet.medicationFollowupMessage.directVisitor,
    sheet.medicationFollowupMessage.lineVisitor,
  ]);
  const patientName = patient ? `${patient.familyName}${patient.givenName}` : 'ー';

  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);
  const resetNotificationPopoverState = useResetAtom(notificationPopoverState);
  const handleClick = useCallback(() => {
    if (!patient) return;

    setPatientDetailDialogState((_state) => ({
      ..._state,
      isOpen: true,
      patientId: patient.id,
      type: 'message' as TabType,
    }));

    // すぐ閉じるとReactのWarningが出るため、一定時間経過後に閉じる
    setTimeout(() => {
      resetNotificationPopoverState();
    }, 200);
  }, [patient, setPatientDetailDialogState, resetNotificationPopoverState]);

  return (
    <NotificationListItem onClick={handleClick} isMarked={!sheet.medicationFollowupMessage.isRead}>
      <Flex flexDirection="column" marginRight={theme.space.m}>
        <Text fontWeight="bold" size="s">
          {`${patientName}様から質問票への回答がありました。回答内容をご確認ください`}
        </Text>
        <Text size="xs" color="grey01" marginTop={theme.space.xs}>
          {`${Label.YYYYMMDDja(sheet.createdAt)} ${Label.HHMM(sheet.createdAt)}`}
        </Text>
      </Flex>
      <Icon icon="forward-line" size="m" marginLeft="auto" />
    </NotificationListItem>
  );
});

Notification.displayName = 'Notification';
